/* eslint-disable react/prop-types */
import {
  appApplicationGroups,
  appApplicationType,
  colors,
  strCode,
  tableDataStatuses
} from "../../../consts";
import { adminUserUtils, appsUtil, userUtil } from "../../../utils";

import {
  FlexContainers,
  GradientLoader,
  StatusIndicator,
  SVG,
  TextOverflowWrapper,
  WrapperContainer,
  TableItemsCounter
} from "../../../components";
import { CellWrapper } from "../../../components/Table/styled";
import { UserIconContainer } from "./styled";

const exactFilter = (rows, columnIds, filterValue) => {
  return !filterValue && filterValue !== false
    ? rows
    : rows.filter((row) => filterValue === row.original[columnIds]);
};

const getAppTypesNames = (appTypes) => {
  return appTypes.map((app) => {
    return appsUtil.getAppName(app);
  });
};

const getUserAppTypes = (groups) => {
  const allAppTypes = (groups ?? []).map((group) => group?.appType);
  const appTypes = [...new Set(allAppTypes)];

  return getAppTypesNames(appTypes);
};

export const UsersTableColumns = [
  {
    Header: strCode.USER_TITLE,
    accessor: "email",
    Cell: ({ cell, cellWidth5, searchedText, isLoading }) => {
      const isAdminUser = adminUserUtils.isUserAdmin(cell.row.original);
      const isActive = cell.row.original?.active;

      const appTypes = isAdminUser
        ? [appApplicationType.value]
        : getUserAppTypes(cell.row.original?.groups);

      const appTypesCount = appTypes.length;

      return (
        <CellWrapper width={`${cellWidth5}px`}>
          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16}>
            {isLoading ? (
              <GradientLoader width={40} height={40} animationDuration={48} borderRadius={8} />
            ) : (
              <UserIconContainer Container={FlexContainers.FlexColumnCenter}>
                {isAdminUser ? <SVG.AdminUserIcon /> : <SVG.UserIcon />}
              </UserIconContainer>
            )}

            <WrapperContainer Container={FlexContainers.FlexColumn} alignItems="start" gap={4}>
              <TextOverflowWrapper
                maxWidth={`${cellWidth5}px`}
                highlightedText={searchedText}
                style={{
                  fontWeight: "600",
                  color: isActive ? colors.RAISIN_100 : colors.RAISIN_50
                }}>
                {isLoading ? (
                  <GradientLoader width={308} height={24} animationDuration={16} />
                ) : (
                  cell.value
                )}
              </TextOverflowWrapper>

              <TextOverflowWrapper
                maxWidth={`${cellWidth5}px`}
                highlightedText={searchedText}
                style={{
                  fontWeight: "500",
                  fontSize: "14px",
                  lineHeight: "26px",
                  color: colors.RAISIN_70
                }}>
                {isLoading ? (
                  <GradientLoader width={172} height={24} animationDuration={24} />
                ) : appTypesCount ? (
                  <>
                    {appTypesCount <= 2 ? (
                      appTypes.join(", ")
                    ) : (
                      <>
                        {appTypes.slice(0, 2).join(", ")}
                        <TableItemsCounter appsCount={appTypesCount - 2} />
                      </>
                    )}
                  </>
                ) : null}
              </TextOverflowWrapper>
            </WrapperContainer>
          </WrapperContainer>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.OWNER_TITLE,
    accessor: "owner",
    Cell: ({ cell, isLoading, cellWidth8 }) => (
      <CellWrapper width={`${cellWidth8}px`}>
        <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
          {isLoading ? (
            <GradientLoader width={132} height={24} animationDuration={24} />
          ) : (
            cell.value
          )}
        </TextOverflowWrapper>
      </CellWrapper>
    )
  },
  {
    Header: strCode.ROLES_LABEL,
    accessor: "groups",
    Cell: ({ cell, isLoading, cellWidth5 }) => {
      const isAdminUser = adminUserUtils.isUserAdmin(cell.row.original);

      const groups = (isAdminUser ? cell.row.original?.roles : cell.value) ?? [];
      const roles = groups.map((group) => {
        if (isAdminUser) {
          return appApplicationGroups.find((item) => item.group === group)?.value ?? group;
        } else {
          return group.name;
        }
      });

      const rolesCount = roles.length;

      return (
        <CellWrapper width={`${cellWidth5}px`}>
          <WrapperContainer>
            <TextOverflowWrapper
              lines={2}
              maxWidth={`${cellWidth5}px`}
              style={{ color: colors.RAISIN_70 }}>
              {isLoading ? (
                <GradientLoader width={308} height={24} animationDuration={16} />
              ) : rolesCount <= 2 ? (
                roles.join(", ")
              ) : (
                <>
                  {roles.slice(0, 2).join(", ")}
                  <TableItemsCounter appsCount={rolesCount - 2} />
                </>
              )}
            </TextOverflowWrapper>
          </WrapperContainer>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.MARKET_TITLE,
    accessor: "markets",
    Cell: ({ cell, isLoading, cellWidth10 }) => {
      const isAdminUser = adminUserUtils.isUserAdmin(cell.row.original);

      const markets = isAdminUser
        ? Array.isArray(cell.row.original?.markets)
          ? cell.row.original?.markets
          : []
        : userUtil.getUserMarkets(cell.row.original?.groups);

      return (
        <CellWrapper width={`${cellWidth10}px`}>
          <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
            {isLoading ? (
              <GradientLoader width={68} height={24} animationDuration={32} />
            ) : Array.isArray(markets) ? (
              markets?.join(", ")
            ) : null}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.STATUS_TITLE,
    id: "active",
    accessor: (d) => {
      const isUserActive = userUtil.getIsActive(d);

      return isUserActive ? "active" : "inactive";
    },
    filter: exactFilter,
    Cell: ({ cell, isLoading }) => (
      <CellWrapper width={"100px"}>
        {isLoading ? (
          <GradientLoader width={82} height={24} animationDuration={32} />
        ) : (
          <StatusIndicator status={tableDataStatuses[cell.value]} />
        )}
      </CellWrapper>
    )
  }
];
